import React, { useCallback, useMemo } from 'react';

import Button from 'components/DesignSystem/Button';
import { buildEmptyState } from 'helper/flux/myStudiesEmptyState';
import { todayInSaoPaulo } from 'helper/flux/myStudiesPeriod';
import { MyStudiesFilters, MyStudiesFiltersChange } from 'types/Flux/Personalized/myStudiesFilters';

export interface EmptyFilterStateProps {
  /** Filtros APLICADOS (`useMyStudiesFilters().filters`): os da resposta vazia. */
  filters: MyStudiesFilters;
  /** Papel de quem vê a tela (o subtítulo de Todos muda para admin). */
  isAdmin: boolean;
  /** Nome do usuário filtrado em Criado por (#2000), para o subtítulo. */
  userLabel?: string;
  /** Recebe a mudança da ação principal ("Ver esta semana", "Ver todos da empresa"…). */
  onFiltersChange: (change: MyStudiesFiltersChange) => void;
  /** "Limpar filtros" (D6). */
  onClearFilters: () => void;
}

/**
 * Estado 3 da lista da aba Meus Estudos, "filtro sem resultado" (#2003, .dc.html:419-429): o
 * cartão no lugar das linhas, com o título, o subtítulo do filtro e as ações de
 * `buildEmptyState` (.dc.html:681-687).
 *
 * - A ação principal amplia a busca um degrau (o período e, já em Tudo, Criado por), e
 *   "Limpar filtros" fica ao lado, secundário, só quando leva a outra lista (`canClear`). Em Tudo
 *   com Todos, "Limpar filtros" é o único botão (o protótipo o repetia); sem nada para limpar,
 *   não há botão.
 * - O subtítulo de "Hoje" leva a data de hoje em São Paulo, lida a cada render.
 * - O título é um heading (h2, abaixo do h1 da página): o leitor de tela chega ao vazio pela
 *   navegação por títulos. A troca é anunciada pela contagem do resumo ("0 estudos", região
 *   `status` do `FilterSummary`), como acontece com as linhas; uma segunda região viva leria o
 *   mesmo fato duas vezes.
 *
 * O foco das ações fica com quem monta: a `ListZone` leva o foco para a zona antes de mudar o
 * filtro, porque o cartão some e o botão clicado cairia no `<body>`.
 */
export default function EmptyFilterState({
  filters,
  isAdmin,
  userLabel,
  onFiltersChange,
  onClearFilters,
}: EmptyFilterStateProps) {
  const todaySP = todayInSaoPaulo(new Date());
  const {
    title, subtitle, widen, canClear,
  } = useMemo(
    () => buildEmptyState(filters, { isAdmin, userLabel, todaySP }),
    [filters, isAdmin, userLabel, todaySP],
  );

  const widenSearch = useCallback(() => {
    if (widen) onFiltersChange(widen.change);
  }, [widen, onFiltersChange]);

  return (
    <div className="my-studies-empty" data-testid="my-studies-empty-filter">
      <h2 className="my-studies-empty-title">{title}</h2>
      <p className="my-studies-empty-text">{subtitle}</p>
      {(widen || canClear) && (
        <div className="my-studies-empty-actions">
          {widen && <Button small onClick={widenSearch}>{widen.label}</Button>}
          {canClear && (
            <Button small type={widen ? 'secondary' : 'primary'} onClick={onClearFilters}>
              Limpar filtros
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
